import {
  parseTree,
  getNodeValue,
  type Node as JNode,
  type ParseError,
} from "jsonc-parser";
import {
  arr,
  fail,
  LIMITS,
  stable,
  type Evidence,
  type Selection,
  type Status,
} from "./types";
import { activeScripts, scriptText, type HNode } from "./html";
export function safeJSON(source: string): any {
  if (source.length > LIMITS.fileBytes)
    fail("invalide", "JSON trop volumineux.");
  let depth = 0,
    quoted = false,
    escaped = false;
  for (const c of source) {
    if (quoted) {
      if (escaped) escaped = false;
      else if (c === "\\") escaped = true;
      else if (c === '"') quoted = false;
    } else if (c === '"') quoted = true;
    else if (c === "{" || c === "[") {
      if (++depth > LIMITS.jsonDepth)
        fail("invalide", "Profondeur JSON supérieure à 40.");
    } else if (c === "}" || c === "]") depth--;
  }
  const errors: ParseError[] = [];
  const tree = parseTree(source, errors, {
    allowTrailingComma: false,
    disallowComments: true,
  });
  if (!tree || errors.length)
    fail("invalide", `JSON invalide (position ${errors[0]?.offset ?? 0}).`);
  let count = 0;
  const q: JNode[] = [tree!];
  while (q.length) {
    const n = q.pop()!;
    if (++count > LIMITS.nodes) fail("invalide", "Trop de nœuds JSON.");
    if (typeof n.value === "string" && n.value.length > LIMITS.valueLength)
      fail("invalide", "Valeur JSON trop longue.");
    if (n.type === "object") {
      const keys = new Set();
      for (const prop of n.children || []) {
        const key = prop.children![0].value;
        if (keys.has(key))
          fail(
            "invalide",
            `Clé JSON dupliquée « ${key} » à la position ${prop.offset}.`,
          );
        if (["__proto__", "constructor", "prototype"].includes(key))
          fail("invalide", `Clé interdite par le lecteur : ${key}.`);
        keys.add(key);
      }
    }
    q.push(...(n.children || []));
  }
  return getNodeValue(tree!);
}
export function urlValue(raw: unknown, base?: string): string {
  if (typeof raw !== "string" || !raw.trim())
    fail("non_determine", "URL absente ou non textuelle.");
  try {
    const u = new URL(raw as string, base);
    if (!["http:", "https:"].includes(u.protocol) || u.username || u.password)
      fail(
        "non_pris_en_charge",
        "Seules les URL HTTP(S) sans identifiants sont prises en charge.",
      );
    return u.href;
  } catch (e) {
    if (e instanceof Error && e.name === "TypeError")
      fail(
        "non_determine",
        "URL relative sans base explicite ou URL invalide.",
      );
    throw e;
  }
}
export function nodeId(raw: unknown, base?: string): string {
  if (typeof raw === "string" && /^_:[A-Za-z0-9_-]+$/.test(raw)) return raw;
  return urlValue(raw, base);
}
const contexts = new Set([
  "https://schema.org",
  "https://schema.org/",
  "http://schema.org",
  "http://schema.org/",
]);
const pointer = (s: string) => s.replace(/~/g, "~0").replace(/\//g, "~1");
type Obj = Record<string, any>;
export type Issue = { script: number; status: Status; reason: string };
export class Graph {
  objects: Obj[] = [];
  byId = new Map<string, Obj[]>();
  locations = new WeakMap<Obj, Evidence>();
  issues: Issue[] = [];
  scripts = 0;
  constructor(
    root: HNode,
    public base?: string,
  ) {
    const scripts = activeScripts(root);
    this.scripts = scripts.length;
    for (const [index, n] of scripts.entries()) {
      try {
        const data = safeJSON(scriptText(n));
        if (!data || typeof data !== "object")
          fail(
            "non_pris_en_charge",
            "Racine JSON-LD scalaire non prise en charge.",
          );
        const found: { o: Obj; p: string }[] = [];
        const q = [{ o: data, p: "", ctx: false }];
        while (q.length) {
          const { o, p, ctx } = q.pop()!;
          if (!o || typeof o !== "object") continue;
          if (Array.isArray(o)) {
            o.forEach((v, i) => q.push({ o: v, p: `${p}/${i}`, ctx }));
            continue;
          }
          let valid = ctx;
          if (o["@context"] !== undefined) {
            if (
              typeof o["@context"] !== "string" ||
              !contexts.has(o["@context"])
            )
              fail(
                "non_pris_en_charge",
                `Contexte personnalisé ou non embarqué (${p || "/"}).`,
              );
            valid = true;
          }
          if (!valid)
            fail(
              "non_pris_en_charge",
              "Contexte Schema.org explicite manquant.",
            );
          for (const key of Object.keys(o)) {
            if (
              key.startsWith("@") &&
              !["@id", "@type", "@context", "@graph"].includes(key)
            )
              fail(
                "non_pris_en_charge",
                `Construction ${key} non prise en charge.`,
              );
            if (key.includes(":"))
              fail(
                "non_pris_en_charge",
                "Propriétés préfixées / IRI longues non prises en charge.",
              );
          }
          if (
            o["@type"] !== undefined &&
            !arr(o["@type"]).every(
              (t) => typeof t === "string" && /^[A-Za-z][\w]*$/.test(t),
            )
          )
            fail("non_pris_en_charge", "Types compacts Schema.org attendus.");
          if (o["@id"] !== undefined && typeof o["@id"] !== "string")
            fail("non_pris_en_charge", "@id doit être une chaîne.");
          found.push({ o, p });
          for (const [k, v] of Object.entries(o))
            if (!k.startsWith("@") || k === "@graph")
              q.push({ o: v, p: `${p}/${pointer(k)}`, ctx: valid });
        }
        if (this.objects.length + found.length > LIMITS.nodes)
          fail("invalide", "Limite cumulée de nœuds JSON-LD dépassée.");
        for (const { o, p } of found) {
          this.objects.push(o);
          this.locations.set(o, {
            script: index + 1,
            pointer: p,
            nodeId: o["@id"],
            value: o,
          });
          if (o["@id"]) {
            try {
              const id = nodeId(o["@id"], base);
              this.byId.set(id, [...(this.byId.get(id) || []), o]);
            } catch {
              /* Resolve only if a rule needs this identity. */
            }
          }
        }
      } catch (e: any) {
        this.issues.push({
          script: index + 1,
          status: e.status || "invalide",
          reason: e.message,
        });
      }
    }
  }
  descriptions(o: Obj): Obj[] {
    if (!o || typeof o !== "object" || Array.isArray(o))
      fail("non_pris_en_charge", "Objet JSON-LD attendu.");
    if (o["@id"]) {
      const matches = this.byId.get(nodeId(o["@id"], this.base)) || [];
      if (matches.length) return matches;
    }
    return [o];
  }
  resolve(o: any): Obj {
    if (!o || typeof o !== "object" || Array.isArray(o))
      fail("non_pris_en_charge", "Référence ou objet imbriqué attendu.");
    const descriptions = this.descriptions(o);
    if (
      !descriptions.some((x) =>
        Object.keys(x).some((k) => !["@id", "@context"].includes(k)),
      )
    )
      fail("non_determine", "Référence non résolue dans ce périmètre.");
    return o;
  }
  values(o: Obj, key: string, proof: Evidence[] = []): any[] {
    const values: any[] = [];
    const seen = new Set();
    for (const d of this.descriptions(o)) {
      if (d[key] === undefined) continue;
      const loc = this.locations.get(d);
      if (loc)
        proof.push({
          ...loc,
          pointer: `${loc.pointer}/${pointer(key)}`,
          value: d[key],
        });
      const signature = stable(d[key]);
      if (!seen.has(signature)) {
        values.push(...arr(d[key]));
        seen.add(signature);
      }
    }
    return values;
  }
  one(o: Obj, key: string, proof: Evidence[] = []): any {
    const v = this.values(o, key, proof);
    if (v.length !== 1)
      fail(
        "non_determine",
        `${key} : ${v.length} valeurs, une attendue${v.length > 1 ? " ; descriptions concurrentes" : ""}.`,
      );
    return v[0];
  }
  types(o: Obj) {
    return this.values(o, "@type") as string[];
  }
  select(selection: Selection, pageUrl?: string): Obj {
    const seen = new Set<string>();
    const candidates: Obj[] = [];
    for (const [index, o] of this.objects.entries()) {
      const id = o["@id"] ? nodeId(o["@id"], this.base) : `anonymous-${index}`;
      if (seen.has(id)) continue;
      seen.add(id);
      if (this.types(o).some((t) => selection.types.includes(t)))
        candidates.push(o);
    }
    const matches = candidates.filter((o) => {
      if (selection.by === "unique") return true;
      if (selection.by === "id")
        return (
          !!o["@id"] &&
          nodeId(o["@id"], this.base) === nodeId(selection.id, this.base)
        );
      if (!pageUrl)
        fail(
          "non_determine",
          "URL de page non déclarée pour sélectionner l’entité.",
        );
      return [
        ...this.values(o, "url"),
        ...this.values(o, "mainEntityOfPage"),
      ].some((v) => {
        const raw = typeof v === "string" ? v : v?.["@id"];
        return (
          raw !== undefined &&
          urlValue(raw, this.base) === urlValue(pageUrl, this.base)
        );
      });
    });
    if (matches.length !== 1)
      fail(
        "non_determine",
        `${matches.length} entités candidates après sélection ; une attendue.`,
      );
    return matches[0];
  }
}
